/**
 * Typed endpoint functions for `/api/auth/*` (docs/APP_PLAN.md §8.1, §12
 * M2). Request shapes are the exact Zod-inferred types from
 * `@perplexicon/validation` — the same schemas `apps/api` validates
 * against server-side — so a request this client is capable of
 * constructing can never disagree with what the server accepts.
 */
import type {
  ChangePasswordRequest,
  LoginRequest,
  PasswordResetComplete,
  PasswordResetRequest,
  RegisterRequest,
} from '@perplexicon/validation';

import type { HttpClient } from './http-client.js';
import type { AuthSessionInfo, CurrentUser } from './types.js';

interface UserEnvelope {
  user: CurrentUser;
}

interface SessionListEnvelope {
  sessions: AuthSessionInfo[];
}

interface MessageEnvelope {
  message: string;
}

export interface AuthApi {
  /** `POST /api/auth/register`. Sets the session cookie on success. */
  register(input: RegisterRequest): Promise<CurrentUser>;
  /** `POST /api/auth/login`. Sets the session cookie on success. */
  login(input: LoginRequest): Promise<CurrentUser>;
  /** `POST /api/auth/logout`. Revokes the current session and clears the cookie. */
  logout(): Promise<void>;
  /** `POST /api/auth/logout-all`. Revokes every session for the current user. */
  logoutAll(): Promise<void>;
  /**
   * `GET /api/auth/me`. Throws `ApiError` with `code: 'UNAUTHENTICATED'`
   * when there is no valid session — callers treat that as "signed out",
   * not as an unexpected failure.
   */
  me(): Promise<CurrentUser>;
  /**
   * `GET /api/auth/sessions`. The caller's own live sessions, newest login
   * first. There is no parameter naming a user — the server reads it from
   * the session cookie — so there is no shape of this call that could ask
   * for someone else's.
   */
  sessions(): Promise<AuthSessionInfo[]>;
  /** `POST /api/auth/password-reset/request`. Always resolves — the response is deliberately non-enumerating. */
  requestPasswordReset(input: PasswordResetRequest): Promise<string>;
  /** `POST /api/auth/password-reset/complete`. */
  completePasswordReset(input: PasswordResetComplete): Promise<string>;
  /** `POST /api/auth/change-password`. Requires an authenticated session. */
  changePassword(input: ChangePasswordRequest): Promise<string>;
}

export function createAuthApi(http: HttpClient): AuthApi {
  return {
    async register(input) {
      const { user } = await http.request<UserEnvelope>({
        method: 'POST',
        path: '/auth/register',
        body: input,
      });
      return user;
    },

    async login(input) {
      const { user } = await http.request<UserEnvelope>({
        method: 'POST',
        path: '/auth/login',
        body: input,
      });
      return user;
    },

    async logout() {
      await http.requestMaybeEmpty<never>({ method: 'POST', path: '/auth/logout' });
    },

    async logoutAll() {
      await http.requestMaybeEmpty<never>({ method: 'POST', path: '/auth/logout-all' });
    },

    async me() {
      const { user } = await http.request<UserEnvelope>({ method: 'GET', path: '/auth/me' });
      return user;
    },

    async sessions() {
      const { sessions } = await http.request<SessionListEnvelope>({
        method: 'GET',
        path: '/auth/sessions',
      });
      return sessions;
    },

    async requestPasswordReset(input) {
      const { message } = await http.request<MessageEnvelope>({
        method: 'POST',
        path: '/auth/password-reset/request',
        body: input,
      });
      return message;
    },

    async completePasswordReset(input) {
      const { message } = await http.request<MessageEnvelope>({
        method: 'POST',
        path: '/auth/password-reset/complete',
        body: input,
      });
      return message;
    },

    async changePassword(input) {
      const { message } = await http.request<MessageEnvelope>({
        method: 'POST',
        path: '/auth/change-password',
        body: input,
      });
      return message;
    },
  };
}
